import * as Sentry from "@sentry/react";

import { H3, Paragraph } from "App/global-style";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import {
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import React from "react";
import { TRAVEL_ABOUT_STEP, TRAVEL_PRICE_STEP } from "../travelSteps";
import {
  coveragesFor,
  getDefaultCoverages,
  getForInsuredOnly,
  getInitialFormStateWithCorrectedCoverage,
} from "./travelProductPriceUtils";

import Accordion, { Row } from "@storeblocks/accordion";
import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { CoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/CoverageCard";
import { CoverageComparisonAccordion } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/CoverageComparisonAccordion";
import { GroupedCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/GroupedCoverageCard";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { TextLanguage } from "Store/texts/interfaces";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { useSelector } from "react-redux";
import { TravelCoverageGroup } from "../travelConstants";
import { TravelPriceFormValues } from "./travelProductPriceInterfaces";

const stepId = TRAVEL_PRICE_STEP.id;

export const TravelProductPrice = (): React.ReactElement => {
  const handleAddProductToCart = useAddProductToCartHandler(stepId);
  const aboutProductState: any = useFormState(TRAVEL_ABOUT_STEP.id);

  const forInsuredOnly = getForInsuredOnly(aboutProductState);
  const { defaultSuperCoverage, defaultQueryCoverage } =
    getDefaultCoverages(forInsuredOnly);
  const initialFormState = getInitialFormStateWithCorrectedCoverage(
    useFormState(stepId, {
      coverage: defaultQueryCoverage,
    }) as TravelPriceFormValues,
    forInsuredOnly,
  );

  const { travel: productCardTextComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.COVERAGE_CARD,
  );

  const [
    {
      formText: { checkPrice },
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.TRAVEL));

  const { addProductToCart } = useSelector((state: ReduxState) => state);

  const coverages = useCoveragePriceCalculations();
  const standardCoverage = coveragesFor(
    forInsuredOnly,
    TravelCoverageGroup.STANDARD,
    coverages,
  )[0];
  const superCoverages = coveragesFor(
    forInsuredOnly,
    TravelCoverageGroup.SUPER,
    coverages,
  );

  const handleSubmit = (formProps: TravelPriceFormValues): any => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.TRAVEL,
    };
    handleAddProductToCart(formProps, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.TRAVEL}`,
        );
      }}
    >
      <ProductPriceTile
        validationSchema={""}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={checkPrice.header[lang]}
      >
        {(formProps: FormikProps<TravelPriceFormValues>) => {
          return (
            <Form>
              <Paragraph>{checkPrice.introText[lang]}</Paragraph>
              <H3>{checkPrice.coverageGroupHeader[lang]}</H3>
              {isDefined(standardCoverage) && (
                <CoverageCard
                  fieldName="coverage"
                  product={ProductBackendName.TRAVEL}
                  coverage={standardCoverage[0]}
                  textComponents={productCardTextComponents}
                  prices={standardCoverage[1].prices}
                />
              )}
              {isDefined(superCoverages) && (
                <GroupedCoverageCard
                  product={ProductBackendName.TRAVEL}
                  fieldName="coverage"
                  formProps={formProps}
                  defaultCoverage={defaultSuperCoverage}
                  groupedCoveragePrices={superCoverages}
                  groupedCoverageText={checkPrice.travelLength}
                  lang={lang}
                  textComponents={productCardTextComponents}
                />
              )}
              <Accordion borderTop={false}>
                <Row
                  header={checkPrice.comparison[lang]}
                  headerProps={{ type: "button" }}
                  id="product-price__comparison-button"
                  data-trackid={buttonTrackId(
                    checkPrice.comparison[TextLanguage.ENGLISH],
                  )}
                >
                  <CoverageComparisonAccordion
                    product={ProductBackendName.TRAVEL}
                    selected={formProps.values.coverage}
                  />
                </Row>
              </Accordion>
              <CurrentPriceMessage
                productBackendName={ProductBackendName.TRAVEL}
                coverages={coverages}
                selectedPrices={[
                  {
                    selectedCoverage: formProps.values.coverage,
                    selectedDeductible: undefined,
                    defaultDeductible: undefined,
                  },
                ]}
              />
              {addProductToCart.error && (
                <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
              )}
              <StyledComparisonWrap>
                <NavigationButtons
                  isUpsell
                  isLoading={addProductToCart.isLoading}
                />
              </StyledComparisonWrap>
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.TRAVEL}
                stepId={stepId}
              />
            </Form>
          );
        }}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
